"use client";

import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper/types";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { normalizePhotoUrl } from "@/lib/helpers";
import { classifyLink } from "@/lib/groups/links";
import type { Testimonial } from "@/lib/testimonials";
import type { GroupsTestimonialsSection as GroupsTestimonialsSectionData } from "@/lib/groups/homepage-sections";
import GroupsTestimonialCard from "./GroupsTestimonialCard";

interface GroupsTestimonialsSectionProps {
  section: GroupsTestimonialsSectionData;
  testimonials: Testimonial[];
}

const FALLBACK_BG = "/groups/img/hero/italy-dark.webp";

export default function GroupsTestimonialsSection({
  section,
  testimonials,
}: GroupsTestimonialsSectionProps) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  if (testimonials.length === 0) return null;

  // Loop needs at least slidesPerView + 1 real slides to be safe.
  const canLoop = testimonials.length > 2;

  const bg = normalizePhotoUrl(section.image) ?? FALLBACK_BG;
  const linkKind = section.actionText ? classifyLink(section.actionLink) : "none";

  // The custom prev/next/pagination elements live below the slides; their refs
  // aren't populated on Swiper's first render pass, so we wire them in
  // `onBeforeInit` (the standard swiper/react pattern for external controls).
  const wireControls = (swiper: SwiperClass) => {
    if (swiper.params.navigation && typeof swiper.params.navigation === "object") {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
    }
    if (swiper.params.pagination && typeof swiper.params.pagination === "object") {
      swiper.params.pagination.el = paginationRef.current;
    }
  };

  return (
    <section
      className="testi-area2 overflow-hidden bg-top-center space background-image"
      id="testi-sec"
      style={{
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "top center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="container">
        <div className="row justify-content-between align-items-center">
          <div className="col-lg-5">
            <div className="title-area text-center text-md-start pe-xl-5 me-xl-5">
              {section.subTitle ? (
                <span className="sub-title2 style1">{section.subTitle}</span>
              ) : null}
              {section.title ? (
                <h2 className="sec-title text-white">{section.title}</h2>
              ) : null}
            </div>
          </div>
          {linkKind !== "none" ? (
            <div className="col-md-auto">
              <div className="sec-btn mt-n3 mt-md-0">
                {/* Hash links are smooth-scrolled by NavigatorScript; only
                    external URLs need to open in a new tab. */}
                <a
                  href={section.actionLink}
                  className="th-btn style3 extra-btn"
                  target={linkKind === "external" ? "_blank" : undefined}
                  rel={
                    linkKind === "external" ? "noopener noreferrer" : undefined
                  }
                >
                  {section.actionText}
                </a>
              </div>
            </div>
          ) : null}
        </div>

        <div className="slider-area">
          <Swiper
            className="testiSlider2 has-shadow"
            modules={[Autoplay, Navigation, Pagination]}
            slidesPerView={1}
            spaceBetween={24}
            speed={1000}
            loop={canLoop}
            autoplay={{ delay: 6500, disableOnInteraction: false }}
            navigation={{ prevEl: null, nextEl: null }}
            pagination={{ el: null, type: "progressbar" }}
            onBeforeInit={wireControls}
            breakpoints={{
              992: { slidesPerView: Math.min(2, testimonials.length) },
            }}
          >
            {testimonials.map((t) => (
              <SwiperSlide key={t.id} className="h-auto">
                <GroupsTestimonialCard testimonial={t} />
              </SwiperSlide>
            ))}

            {/* `slot="container-end"` renders this inside `.swiper` but OUTSIDE
                `.swiper-wrapper`, so Swiper doesn't treat it as a slide. */}
            <div slot="container-end" className="slider-controller">
              <button
                ref={prevRef}
                type="button"
                className="slider-arrow default slider-prev"
                aria-label="Previous testimonial"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/groups/img/icon/left-arrow2.svg"
                  alt=""
                  width={18}
                  height={14}
                  loading="lazy"
                  decoding="async"
                  aria-hidden="true"
                />
              </button>
              <div ref={paginationRef} className="slider-pagination" />
              <button
                ref={nextRef}
                type="button"
                className="slider-arrow default slider-next"
                aria-label="Next testimonial"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/groups/img/icon/right-arrow2.svg"
                  alt=""
                  width={18}
                  height={14}
                  loading="lazy"
                  decoding="async"
                  aria-hidden="true"
                />
              </button>
            </div>
          </Swiper>
        </div>
      </div>
    </section>
  );
}
